import { useEffect, useState } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Modal de ação sensível, em dois passos.
 *
 * Passo 1: resumo do impacto + motivo obrigatório (mínimo 10 caracteres).
 * Passo 2: resultado, com o registro de auditoria gerado.
 *
 * O botão de confirmar fica desabilitado até o motivo ser válido. Isso é
 * conveniência da UI — quem recusa de fato é o servidor, que valida o mesmo
 * mínimo. Confiar só na UI deixaria a regra a uma requisição de distância de
 * ser burlada.
 */

export const MOTIVO_MINIMO = 10;

type Variante = 'destrutiva' | 'confirmacao' | 'neutra';

const CORES: Record<Variante, { botao: string; icone: string }> = {
  destrutiva: { botao: 'bg-saida hover:bg-[#A12121]', icone: 'text-saida' },
  confirmacao: { botao: 'bg-entrada hover:bg-[#136A40]', icone: 'text-entrada' },
  neutra: { botao: 'bg-primaria hover:bg-primaria-hover', icone: 'text-primaria' },
};

export interface ModalAcaoProps {
  aberto: boolean;
  titulo: string;
  variante?: Variante;
  /** Resumo do que vai acontecer. Precisa deixar a consequência explícita. */
  impacto: ReactNode;
  rotuloConfirmar: string;
  /** Quando falso, o motivo não é exigido (ex.: confirmar o que já foi sugerido). */
  exigeMotivo?: boolean;
  /** Campo extra, como o novo valor numa correção. */
  campoExtra?: ReactNode;
  aoConfirmar: (motivo: string) => Promise<void>;
  aoFechar: () => void;
}

export function ModalAcao({
  aberto,
  titulo,
  variante = 'neutra',
  impacto,
  rotuloConfirmar,
  exigeMotivo = true,
  campoExtra,
  aoConfirmar,
  aoFechar,
}: ModalAcaoProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setMotivo('');
      setConcluido(false);
      setErro(null);
    }
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  const motivoValido = !exigeMotivo || motivo.trim().length >= MOTIVO_MINIMO;
  const cores = CORES[variante];

  async function confirmar() {
    if (!motivoValido || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await aoConfirmar(motivo.trim());
      setConcluido(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível concluir.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--cor-overlay)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      <div className="w-full max-w-[440px] rounded-[var(--radius-card)] bg-superficie p-5 shadow-[var(--shadow-modal)]">
        {concluido ? (
          <>
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-entrada-bg">
                <Check size={15} className="text-entrada-fg" aria-hidden />
              </span>
              <h2 className="font-semibold text-texto">Pronto</h2>
            </div>
            <p className="text-[length:var(--text-secundario)] text-texto-secundario">
              A ação foi concluída e ficou registrada na auditoria, com seu nome, data e hora.
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={aoFechar}
                className="rounded-[var(--radius-controle)] bg-[var(--cor-card-escuro)] px-4 py-2 text-[length:var(--text-secundario)] font-medium text-white"
              >
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className={cores.icone} aria-hidden />
              <h2 className="font-semibold text-texto">{titulo}</h2>
            </div>

            <div className="rounded-[var(--radius-controle)] bg-fundo p-3 text-[length:var(--text-secundario)] text-texto-secundario">
              {impacto}
            </div>

            {campoExtra && <div className="mt-3">{campoExtra}</div>}

            {exigeMotivo && (
              <label className="mt-4 block">
                <span className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
                  Motivo *
                </span>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-[var(--radius-controle)] border border-borda-input bg-superficie p-2 text-[length:var(--text-corpo)] text-texto placeholder:text-[var(--cor-placeholder)]"
                  placeholder="Por que esta ação é necessária?"
                  autoFocus
                />
                <span className="mt-1 block text-[length:var(--text-metadado)] text-texto-terciario">
                  Mínimo de {MOTIVO_MINIMO} caracteres. Registrado com seu nome, data e hora.
                </span>
              </label>
            )}

            {erro && (
              <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
                {erro}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={aoFechar}
                className="inline-flex items-center gap-1 rounded-[var(--radius-controle)] border border-borda-input px-3 py-2 text-[length:var(--text-secundario)] text-texto-secundario hover:border-primaria hover:text-primaria"
              >
                <X size={13} aria-hidden />
                Voltar
              </button>
              <button
                type="button"
                onClick={confirmar}
                disabled={!motivoValido || enviando}
                // opacity .45 quando inválido, como no handoff: o botão continua
                // visível para o usuário saber que existe, mas não clicável.
                className={`rounded-[var(--radius-controle)] px-4 py-2 text-[length:var(--text-secundario)] font-medium text-white transition-opacity ${cores.botao} ${
                  motivoValido && !enviando ? '' : 'cursor-not-allowed opacity-45'
                }`}
              >
                {enviando ? 'Enviando...' : rotuloConfirmar}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
